import React, { useState } from 'react'
import Modal from './Modal'
import { Field, inputClass } from './Field'
import { IncomeRule, ExpenseRule, BudgetItem, CapitalRepayment, DateRuleType, Frequency, RepaymentEffect } from '../types'
import { makeId } from '../lib/id'
import { applyCapitalRepayment, describeRepaymentEffect, repaymentEffectBlocker, type RepaymentInput } from '../lib/loanEngine'
import { formatMoney } from '../lib/format'
import { fromISODate } from '../lib/dateRules'
import { useSuggestedNameAndCategory } from './SuggestedFields'
import { Combobox } from './Combobox'
import { ShareNameCheckbox } from './ShareNameCheckbox'

type Kind = 'income' | 'expense' | 'budget'
type Rule = IncomeRule | ExpenseRule | BudgetItem

interface Props {
  kind: Kind
  initial?: Rule
  onSave: (item: Rule, addAnother: boolean) => void
  onDelete?: () => void
  onClose: () => void
}

const KIND_META: Record<Kind, { title: string; amountLabel: string }> = {
  income: { title: 'Income', amountLabel: 'Amount received' },
  expense: { title: 'Fixed outgoing', amountLabel: 'Amount paid' },
  budget: { title: 'Budgeted spend', amountLabel: 'Forecast amount' },
}

export const MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export const DOW_OPTIONS = [
  { v: 1, l: 'Monday' },
  { v: 2, l: 'Tuesday' },
  { v: 3, l: 'Wednesday' },
  { v: 4, l: 'Thursday' },
  { v: 5, l: 'Friday' },
  { v: 6, l: 'Saturday' },
  { v: 0, l: 'Sunday' },
]

export default function RuleForm({ kind, initial, onSave, onDelete, onClose }: Props) {
  const isExpense = kind === 'expense'
  const asExpense = initial as ExpenseRule | undefined

  const { name, setName, category, setCategory, nameOptions, categoryOptions, share, setShare, shareOnSave } =
    useSuggestedNameAndCategory(kind, initial?.name ?? '', initial?.category ?? '')
  const [amount, setAmount] = useState(initial?.amount?.toString() ?? '')
  const [frequency, setFrequency] = useState<Frequency>(
    (initial as IncomeRule | ExpenseRule)?.frequency ?? 'monthly',
  )
  const [dayOfMonth, setDayOfMonth] = useState<string>(
    initial && 'dateRule' in initial && initial.dateRule
      ? initial.dateRule.dayOfMonth === 'last'
        ? 'last'
        : String(initial.dateRule.dayOfMonth)
      : '1',
  )
  const [dateRuleType, setDateRuleType] = useState<DateRuleType>(
    (initial && 'dateRule' in initial && initial.dateRule?.type) || 'exact',
  )
  const [monthOfYear, setMonthOfYear] = useState<number>(
    (initial && 'monthOfYear' in initial && initial.monthOfYear) || new Date().getMonth() + 1,
  )
  const [dayOfWeek, setDayOfWeek] = useState<number>(initial?.dayOfWeek ?? 1)
  const [startDate, setStartDate] = useState(initial && 'startDate' in initial ? initial.startDate ?? '' : '')
  const [endDate, setEndDate] = useState(initial && 'endDate' in initial ? initial.endDate ?? '' : '')
  const [active, setActive] = useState(initial?.active ?? true)
  const [isDebt, setIsDebt] = useState(asExpense?.isDebt ?? false)
  const [interestRateApr, setInterestRateApr] = useState(asExpense?.interestRateApr?.toString() ?? '')
  const [principal, setPrincipal] = useState(asExpense?.principal?.toString() ?? '')
  const [principalAsOf, setPrincipalAsOf] = useState(asExpense?.principalAsOf ?? '')
  // Repayments already on the loan, and ones added in this form: those are
  // applied, effect and all, when it is saved (SM-41).
  const [repayments, setRepayments] = useState<CapitalRepayment[]>(asExpense?.capitalRepayments ?? [])
  const [added, setAdded] = useState<(RepaymentInput & { id: string })[]>([])
  const [repaymentDate, setRepaymentDate] = useState('')
  const [repaymentAmount, setRepaymentAmount] = useState('')
  const [repaymentEffect, setRepaymentEffect] = useState<RepaymentEffect>('shorten')

  const showYearly = frequency === 'yearly'
  const showMonthly = frequency === 'monthly' || showYearly
  const showWeekly = frequency === 'weekly' || frequency === 'four_weekly'

  function baseFields() {
    return {
      id: initial?.id ?? makeId(),
      name: name.trim() || 'Untitled',
      amount: Math.abs(parseFloat(amount) || 0),
      active,
      category: category.trim() || undefined,
    }
  }

  function commonFields() {
    return {
      ...baseFields(),
      frequency,
      dateRule: showMonthly ? { type: dateRuleType, dayOfMonth: dayOfMonth === 'last' ? ('last' as const) : Number(dayOfMonth) } : { type: 'exact' as const, dayOfMonth: 1 },
      monthOfYear: showYearly ? monthOfYear : undefined,
      dayOfWeek: showWeekly ? dayOfWeek : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }
  }

  /** The outgoing as the form has it, before the repayments added here are applied. */
  function expenseAsEntered(): ExpenseRule {
    return {
      ...commonFields(),
      kind: 'expense',
      isDebt,
      interestRateApr: isDebt && interestRateApr ? parseFloat(interestRateApr) : undefined,
      principal: isDebt && principal ? parseFloat(principal) : undefined,
      principalAsOf: isDebt && principalAsOf ? principalAsOf : undefined,
      capitalRepayments: isDebt && repayments.length > 0 ? repayments : undefined,
      amountChanges: asExpense?.amountChanges,
    }
  }

  /** Each added repayment applied in date order, with what it did. */
  function withAdded(): { loan: ExpenseRule; effects: Map<string, string> } {
    let loan = expenseAsEntered()
    const effects = new Map<string, string>()
    for (const r of [...added].sort((a, b) => a.date.localeCompare(b.date))) {
      const next = applyCapitalRepayment(loan, r)
      effects.set(r.id, describeRepaymentEffect(loan, next, r.date))
      loan = next
    }
    return { loan, effects }
  }

  function addRepayment() {
    const value = Math.abs(parseFloat(repaymentAmount) || 0)
    if (!repaymentDate || value <= 0) return
    const effect = repaymentEffect === 'lower' && repaymentEffectBlocker(expenseAsEntered(), 'lower') ? 'shorten' : repaymentEffect
    setAdded((list) => [...list, { id: makeId(), date: repaymentDate, amount: value, effect }])
    setRepaymentDate('')
    setRepaymentAmount('')
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null
    const addAnother = submitter?.dataset.action === 'add-another'
    shareOnSave()

    if (kind === 'budget') {
      const item: BudgetItem = {
        ...baseFields(),
        kind: 'budget',
        frequency: frequency === 'weekly' ? 'weekly' : 'monthly',
        dateRule: showMonthly ? { type: dateRuleType, dayOfMonth: dayOfMonth === 'last' ? 'last' : Number(dayOfMonth) } : undefined,
        dayOfWeek: showWeekly ? dayOfWeek : undefined,
      }
      onSave(item, addAnother)
      return
    }

    if (kind === 'income') {
      const item: IncomeRule = { ...commonFields(), kind: 'income' }
      onSave(item, addAnother)
    } else {
      onSave(isDebt ? withAdded().loan : expenseAsEntered(), addAnother)
    }
  }

  const latestChange = [...(asExpense?.amountChanges ?? [])].sort((x, y) => y.from.localeCompare(x.from))[0]
  const amountChangeHint = latestChange
    ? `From ${fromISODate(latestChange.from).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })} it is ${formatMoney(latestChange.amount)}, after an overpayment; this is the amount before then`
    : undefined

  function renderRepayments() {
    const draft = expenseAsEntered()
    const { effects } = withAdded()
    const blocked = { shorten: repaymentEffectBlocker(draft, 'shorten'), lower: repaymentEffectBlocker(draft, 'lower') }
    const effect = blocked.lower && repaymentEffect === 'lower' ? 'shorten' : repaymentEffect
    const shortDate = (iso: string) => fromISODate(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    const effectLabel = (e: RepaymentEffect) => (e === 'lower' ? 'lower payment' : 'shorter term')

    return (
      <div className="mt-3" role="group" aria-label="Capital repayments">
        <p className="text-sm font-medium text-ink mb-1">Capital repayments</p>
        <p className="text-xs text-ink/75 mb-2">
          Overpayments that lower the balance. Added here they aren&rsquo;t taken from the forecast as a payment; add a one-off for that.
        </p>
        {(repayments.length > 0 || added.length > 0) && (
          <ul className="text-sm mb-2 space-y-1">
            {repayments.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-2">
                <span>
                  {shortDate(r.date)}: {formatMoney(r.amount)} ({effectLabel(r.effect)})
                </span>
                <button
                  type="button"
                  className="text-xs text-rag-bad hover:underline"
                  aria-label={`Remove the ${formatMoney(r.amount)} repayment on ${shortDate(r.date)}`}
                  onClick={() => setRepayments((list) => list.filter((x) => x.id !== r.id))}
                >
                  Remove
                </button>
              </li>
            ))}
            {added.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-2">
                <span>
                  {shortDate(r.date)}: {formatMoney(r.amount)} ({effectLabel(r.effect)}, new)
                  <span className="block text-xs text-ink/75">{effects.get(r.id)}</span>
                </span>
                <button
                  type="button"
                  className="text-xs text-rag-bad hover:underline"
                  aria-label={`Remove the ${formatMoney(r.amount)} repayment on ${shortDate(r.date)}`}
                  onClick={() => setAdded((list) => list.filter((x) => x.id !== r.id))}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        {repayments.length > 0 && (
          <p className="text-xs text-ink/75 mb-2">Removing a saved repayment doesn&rsquo;t change the end date or payment back.</p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Repayment date">
            <input className={inputClass} type="date" value={repaymentDate} onChange={(e) => setRepaymentDate(e.target.value)} />
          </Field>
          <Field label="Repayment amount">
            <input className={inputClass} type="number" step="0.01" min="0" value={repaymentAmount} onChange={(e) => setRepaymentAmount(e.target.value)} />
          </Field>
        </div>
        <Field label="After it, the lender will" hint={blocked[effect] ?? undefined}>
          <select className={inputClass} value={effect} onChange={(e) => setRepaymentEffect(e.target.value as RepaymentEffect)}>
            <option value="shorten">Shorten the term (same payment)</option>
            <option value="lower" disabled={!!blocked.lower}>
              {blocked.lower ? `Lower the payment (${blocked.lower.toLowerCase()})` : 'Lower the payment (same end date)'}
            </option>
          </select>
        </Field>
        <button
          type="button"
          onClick={addRepayment}
          disabled={!repaymentDate || !(parseFloat(repaymentAmount) > 0)}
          className="px-3 py-1.5 text-sm rounded-sm border border-accent text-accent hover:bg-accent/10 disabled:opacity-50"
        >
          Add repayment
        </button>
      </div>
    )
  }

  return (
    <Modal title={`${initial ? 'Edit' : 'Add'} ${KIND_META[kind].title.toLowerCase()}`} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <Combobox label="Name" value={name} onChange={setName} options={nameOptions} required autoFocus />

        <Field label={KIND_META[kind].amountLabel} hint={amountChangeHint}>
          <input
            className={inputClass}
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </Field>

        <Field label="Frequency">
          <select className={inputClass} value={frequency} onChange={(e) => setFrequency(e.target.value as Frequency)}>
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            {kind !== 'budget' && <option value="four_weekly">Every 4 weeks</option>}
            {kind !== 'budget' && <option value="yearly">Yearly</option>}
          </select>
        </Field>

        {showYearly && (
          <Field label="Month" hint="e.g. the month an annual insurance premium renews">
            <select className={inputClass} value={monthOfYear} onChange={(e) => setMonthOfYear(Number(e.target.value))}>
              {MONTH_OPTIONS.map((l, i) => (
                <option key={l} value={i + 1}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
        )}

        {showMonthly && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Day of month">
              <select className={inputClass} value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
                <option value="last">Last day</option>
              </select>
            </Field>
            <Field label="Rule">
              <select className={inputClass} value={dateRuleType} onChange={(e) => setDateRuleType(e.target.value as DateRuleType)}>
                <option value="exact">Exact date</option>
                <option value="working_day_before">Working day before</option>
                <option value="working_day_after">Working day after</option>
              </select>
            </Field>
          </div>
        )}

        {showWeekly && (
          <Field label="Day of week">
            <select className={inputClass} value={dayOfWeek} onChange={(e) => setDayOfWeek(Number(e.target.value))}>
              {DOW_OPTIONS.map((d) => (
                <option key={d.v} value={d.v}>
                  {d.l}
                </option>
              ))}
            </select>
          </Field>
        )}

        {kind !== 'budget' && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts (optional)">
              <input className={inputClass} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label="Ends (optional)" hint={isExpense ? 'e.g. final loan repayment date' : undefined}>
              <input className={inputClass} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Field>
          </div>
        )}

        <Combobox
          label="Category (optional)"
          value={category}
          onChange={setCategory}
          options={categoryOptions}
          placeholder={kind === 'income' ? 'e.g. Income, Benefits' : 'e.g. Housing, Food, Transport'}
        />

        {isExpense && (
          <div className="border-t border-line pt-3 mt-1 mb-4">
            <label className="flex items-center gap-2 text-sm text-ink mb-3">
              <input type="checkbox" checked={isDebt} onChange={(e) => setIsDebt(e.target.checked)} />
              This is a loan or debt (track interest for payoff modelling)
            </label>
            {isDebt && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Interest rate (APR %)">
                  <input className={inputClass} type="number" step="0.01" value={interestRateApr} onChange={(e) => setInterestRateApr(e.target.value)} />
                </Field>
                <Field label="Outstanding principal">
                  <input className={inputClass} type="number" step="0.01" value={principal} onChange={(e) => setPrincipal(e.target.value)} />
                </Field>
                <Field label="Balance as of (optional)" hint="The date the principal was right; repayments from then lower it">
                  <input className={inputClass} type="date" value={principalAsOf} onChange={(e) => setPrincipalAsOf(e.target.value)} />
                </Field>
              </div>
            )}
            {isDebt && renderRepayments()}
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-ink mb-3">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>

        <ShareNameCheckbox checked={share} onChange={setShare} />

        <div className="flex items-center justify-between pt-2 border-t border-line">
          <div>
            {onDelete && (
              <button type="button" onClick={onDelete} className="text-sm text-rag-bad hover:underline">
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-sm border border-line text-ink/80 hover:bg-paper">
              Cancel
            </button>
            {!initial && (
              <button
                type="submit"
                data-action="add-another"
                className="px-3 py-1.5 text-sm rounded-sm border border-accent text-accent hover:bg-accent/10"
              >
                Save &amp; add another
              </button>
            )}
            <button type="submit" data-action="save" className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90">
              Save
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
